/**
 * CmsNavMenusPage — Navigation Menus list
 *
 * Lists all navigation menus for a space (header, footer, sidebar, etc.)
 * and allows creating / deleting them.
 * Clicking a menu opens the item editor at /spaces/:spaceId/menus/:menuId/items
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigation, Plus, Trash2, ChevronRight } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface NavMenu {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateMenuForm {
  name: string;
  slug: string;
}

const EMPTY_FORM: CreateMenuForm = { name: '', slug: '' };

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsNavMenusPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/nav/menus`;

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<NavMenu | null>(null);
  const [form, setForm] = useState<CreateMenuForm>(EMPTY_FORM);
  const [autoSlug, setAutoSlug] = useState(true);

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: menus = [], isLoading } = useQuery<NavMenu[]>({
    queryKey: ['cms-nav-menus', spaceId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId,
  });

  // ── Mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (body: CreateMenuForm) => api.post(BASE, body).then((r) => r.data as NavMenu),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-nav-menus', spaceId] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setAutoSlug(true);
      toast.success('Menu created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create menu')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-nav-menus', spaceId] });
      setDeleteTarget(null);
      toast.success('Menu deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleNameChange(name: string) {
    setForm((f) => ({
      ...f,
      name,
      slug: autoSlug ? slugify(name) : f.slug,
    }));
  }

  function handleSlugChange(slug: string) {
    setAutoSlug(false);
    setForm((f) => ({ ...f, slug }));
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Navigation Menus"
        description="Create named menus (Header, Footer, Sidebar…) and manage their items independently."
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Menu
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={3} cols={3} />
        </div>
      ) : menus.length === 0 ? (
        <EmptyState
          icon={<Navigation size={26} />}
          title="No menus yet"
          description="Create a menu (e.g. 'Header', 'Footer') to manage navigation items independently"
          action={{ label: 'New Menu', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100">
          {menus.map((menu) => (
            <button
              key={menu.id}
              onClick={() => navigate(`/spaces/${spaceId}/menus/${menu.id}/items`)}
              className="flex items-center justify-between px-5 py-4 w-full text-left hover:bg-zinc-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 group-hover:bg-zinc-900 group-hover:text-white transition-colors flex-shrink-0">
                  <Navigation size={15} />
                </div>
                <div>
                  <div className="text-sm font-medium text-zinc-900">{menu.name}</div>
                  <div className="text-xs text-zinc-400 font-mono mt-0.5">{menu.slug}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(menu);
                  }}
                  className="p-1.5 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete menu"
                >
                  <Trash2 size={13} />
                </button>
                <ChevronRight size={15} className="text-zinc-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setForm(EMPTY_FORM);
          setAutoSlug(true);
        }}
        title="New Navigation Menu"
      >
        <div className="space-y-4">
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Header Navigation"
          />
          <Input
            label="Slug *"
            value={form.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="header"
            hint="Used to reference this menu in layouts and portal code"
          />
        </div>

        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}

        <ModalFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowCreate(false);
              setForm(EMPTY_FORM);
              setAutoSlug(true);
            }}
          >
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!form.name.trim() || !form.slug.trim()}
            onClick={() => createMutation.mutate(form)}
          >
            Create Menu
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete menu?"
        message={`"${deleteTarget?.name}" and all its items will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
